import React, { useEffect, useState, useCallback } from "react";
import { Loader2, Check, X, IndianRupee, Trophy, ExternalLink, Play } from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CreatorLeaderboardAdmin = ({ authAxios }) => {
  const [pending, setPending] = useState([]);
  const [payouts, setPayouts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [verifyFor, setVerifyFor] = useState(null); // content doc being verified

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [a, b] = await Promise.all([
        authAxios.get(`${API_URL}/api/creators/admin/content/pending`),
        authAxios.get(`${API_URL}/api/creators/admin/payouts`),
      ]);
      setPending(a.data.items || []);
      setPayouts(b.data.items || []);
    } catch { /* ignore */ }
    setLoading(false);
  }, [authAxios]);

  useEffect(() => { load(); }, [load]);

  const runPayouts = async () => {
    const d = new Date();
    const y = d.getUTCFullYear();
    const m = d.getUTCMonth() === 0 ? 12 : d.getUTCMonth();
    const year = d.getUTCMonth() === 0 ? y - 1 : y;
    const ok = window.confirm(`Generate payouts snapshot for ${year}-${String(m).padStart(2, "0")}? (top 3 only, idempotent)`);
    if (!ok) return;
    setRunning(true);
    try {
      await authAxios.post(`${API_URL}/api/creators/admin/payouts/run/${year}/${m}`);
      await load();
    } catch (e) { alert(e?.response?.data?.detail || "Failed"); }
    setRunning(false);
  };

  const markPaid = async (p) => {
    const ref = window.prompt(`Payment reference (UTR / cheque no) for ${p.creator_name} — ₹${p.amount}?`);
    if (!ref || ref.length < 4) return;
    try {
      await authAxios.put(`${API_URL}/api/creators/admin/payouts/${p.id}/mark-paid`, { payment_reference: ref });
      await load();
    } catch (e) { alert(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="space-y-4" data-testid="creator-leaderboard-admin">
      {/* Pending content */}
      <div className="bg-white rounded-2xl p-5 border border-gray-100 shadow-sm">
        <div className="flex items-center justify-between mb-3">
          <div>
            <h3 className="font-bold text-[#1E1B4B] text-sm flex items-center gap-2">
              <Play size={14} className="text-[#E83E8C]" /> Pending content verification
            </h3>
            <p className="text-xs text-gray-500">Creators submit reels → you verify views → they earn points.</p>
          </div>
          <span className="text-[10px] bg-amber-50 text-amber-700 px-2 py-1 rounded-full font-bold" data-testid="pending-count">
            {pending.length} pending
          </span>
        </div>
        {loading ? (
          <div className="flex justify-center py-6"><Loader2 size={16} className="animate-spin text-[#E83E8C]" /></div>
        ) : pending.length === 0 ? (
          <p className="text-xs text-gray-500 text-center py-4">No pending submissions 🎉</p>
        ) : (
          <div className="space-y-1.5">
            {pending.map((c) => (
              <div key={c.id} className="flex items-center gap-2 bg-gray-50 rounded-lg px-2 py-1.5" data-testid={`pending-${c.id}`}>
                <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-gray-200 text-gray-700 capitalize">{c.platform}</span>
                <a href={c.url} target="_blank" rel="noreferrer" className="flex-1 truncate text-xs text-[#1E1B4B] hover:text-[#E83E8C] flex items-center gap-1">
                  {c.url} <ExternalLink size={10} />
                </a>
                <span className="text-[10px] text-gray-500">by {c.creator_name}</span>
                <span className="text-[10px] text-gray-400">est: {(c.estimated_views || 0).toLocaleString()}</span>
                <button
                  onClick={() => setVerifyFor(c)}
                  className="px-2 py-1 rounded-lg bg-emerald-500 text-white text-[10px] font-bold hover:bg-emerald-600"
                  data-testid={`verify-${c.id}`}
                >
                  Verify
                </button>
                <button
                  onClick={async () => {
                    if (!window.confirm("Reject this submission?")) return;
                    await authAxios.put(`${API_URL}/api/creators/admin/content/${c.id}/verify`, { verified_views: 0, approve: false, note: "" });
                    load();
                  }}
                  className="px-2 py-1 rounded-lg bg-red-500 text-white text-[10px] font-bold hover:bg-red-600"
                  data-testid={`reject-${c.id}`}
                >
                  Reject
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Payouts */}
      <div className="bg-white rounded-2xl p-5 border border-gray-100 shadow-sm">
        <div className="flex items-center justify-between mb-3">
          <div>
            <h3 className="font-bold text-[#1E1B4B] text-sm flex items-center gap-2">
              <IndianRupee size={14} className="text-emerald-600" /> Monthly payouts
            </h3>
            <p className="text-xs text-gray-500">Top 3 this-month leaders earn ₹5k · ₹3k · ₹2k.</p>
          </div>
          <button
            onClick={runPayouts}
            disabled={running}
            className="flex items-center gap-1 px-3 py-1.5 rounded-lg bg-[#E83E8C] text-white text-[11px] font-bold hover:bg-[#c22d78] disabled:opacity-50"
            data-testid="run-payouts-btn"
          >
            {running ? <Loader2 size={12} className="animate-spin" /> : <Trophy size={12} />}
            Snapshot last month
          </button>
        </div>
        {payouts.length === 0 ? (
          <p className="text-xs text-gray-500 text-center py-4">No payouts recorded yet.</p>
        ) : (
          <div className="space-y-1.5">
            {payouts.map((p) => (
              <div key={p.id} className="flex items-center gap-2 bg-gray-50 rounded-lg px-2 py-1.5 text-xs" data-testid={`payout-${p.id}`}>
                <span className="font-mono font-bold text-gray-500">{p.month_key}</span>
                <span className={`px-1.5 py-0.5 rounded-full text-[9px] font-bold ${p.rank === 1 ? "bg-yellow-100 text-yellow-700" : p.rank === 2 ? "bg-slate-100 text-slate-700" : "bg-amber-100 text-amber-700"}`}>
                  #{p.rank}
                </span>
                <span className="flex-1 truncate font-bold text-[#1E1B4B]">{p.creator_name}</span>
                <span className="font-bold text-emerald-600">₹{p.amount.toLocaleString()}</span>
                {p.status === "paid" ? (
                  <span className="px-1.5 py-0.5 rounded-full bg-emerald-100 text-emerald-700 text-[9px] font-bold flex items-center gap-0.5">
                    <Check size={10} /> Paid · {p.payment_reference}
                  </span>
                ) : (
                  <button
                    onClick={() => markPaid(p)}
                    className="px-2 py-1 rounded-lg bg-emerald-500 text-white text-[10px] font-bold hover:bg-emerald-600"
                    data-testid={`mark-paid-${p.id}`}
                  >
                    Mark paid
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {verifyFor && (
        <VerifyModal
          doc={verifyFor}
          onClose={() => setVerifyFor(null)}
          onDone={async (views, note) => {
            await authAxios.put(`${API_URL}/api/creators/admin/content/${verifyFor.id}/verify`, {
              verified_views: views, approve: true, note,
            });
            setVerifyFor(null);
            load();
          }}
        />
      )}
    </div>
  );
};

const VerifyModal = ({ doc, onClose, onDone }) => {
  const [views, setViews] = useState(String(doc.estimated_views || ""));
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose} data-testid="verify-modal">
      <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-2xl p-5 max-w-sm w-full">
        <h3 className="font-bold text-[#1E1B4B] mb-1">Verify content</h3>
        <p className="text-xs text-gray-500 mb-3 truncate">{doc.url}</p>

        <label className="block text-[11px] uppercase tracking-wider font-bold text-gray-500 mb-1">Verified view count</label>
        <input type="number" value={views} onChange={(e) => setViews(e.target.value)} className="w-full bg-gray-50 rounded-xl px-3 py-2 text-sm outline-none mb-3" data-testid="verify-views-input" />

        <label className="block text-[11px] uppercase tracking-wider font-bold text-gray-500 mb-1">Note (optional)</label>
        <input value={note} onChange={(e) => setNote(e.target.value)} className="w-full bg-gray-50 rounded-xl px-3 py-2 text-sm outline-none mb-4" data-testid="verify-note-input" />

        <div className="flex gap-2">
          <button onClick={onClose} className="flex-1 py-2.5 rounded-xl bg-gray-100 text-gray-700 text-sm font-bold">
            <X size={14} className="inline mr-1" /> Cancel
          </button>
          <button
            onClick={async () => { setSaving(true); await onDone(parseInt(views || "0"), note); setSaving(false); }}
            disabled={saving}
            className="flex-1 py-2.5 rounded-xl bg-emerald-500 text-white text-sm font-bold hover:bg-emerald-600 disabled:opacity-50"
            data-testid="verify-approve-btn"
          >
            {saving ? <Loader2 size={14} className="animate-spin inline mr-1" /> : <Check size={14} className="inline mr-1" />} Approve
          </button>
        </div>
      </div>
    </div>
  );
};

export default CreatorLeaderboardAdmin;
